import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Smartphone, Video, Brain, Trophy, Gift, 
  Target, Zap, ChevronRight, Play,
  CheckCircle, ArrowRight, Sparkles, Medal,
  TrendingUp, Gamepad2, Camera, BookOpen
} from 'lucide-react';
import { Button, CardCorner, AnimatedBorder, RegistrationWizard } from '../ui';
import { STORE_LINKS } from '../../constants/storeLinks';
import { detectBrowserCountry, formatMoney } from '../../utils/detectCountry';
import { getPaymentCatalog, type PaymentCatalog } from '../../services/api/paymentCatalogService';

export function ClubTalentstar() {
  const { t } = useTranslation();
  const [selectedFeature, setSelectedFeature] = useState(0);
  const [showPlans, setShowPlans] = useState(false);
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [catalog, setCatalog] = useState<PaymentCatalog | null>(null);

  useEffect(() => {
    getPaymentCatalog(detectBrowserCountry()).then(setCatalog).catch(() => setCatalog(null));
  }, []);

  const APP_FEATURES = [
    {
      id: 'tutorials',
      icon: BookOpen,
      title: t('club.tutorials'),
      description: t('club.tutorialsDesc'),
      color: 'from-cyan-500 to-blue-600',
      skills: [
        t('club.skillShot'),
        t('club.skillSpeed'),
        t('club.skillDribble'),
        t('club.skillControl'),
        t('club.skillPassing'),
        t('club.skillAgility'),
      ],
      levels: [
        t('club.beginner'),
        t('club.levelEasy'),
        t('club.levelIntermediate'),
        t('club.levelAdvanced'),
        t('club.levelExpert'),
      ],
    },
    {
      id: 'recording',
      icon: Camera,
      title: t('club.recording'),
      description: t('club.recordingDesc'),
      color: 'from-green-500 to-emerald-600',
      features: [
        t('club.feature33Points'),
        t('club.featureAutoJump'),
        t('club.featureJumpHeight'),
        t('club.featureSkeletonVideo'),
      ],
    },
    {
      id: 'evaluation',
      icon: Trophy,
      title: t('club.evaluation'),
      description: t('club.evaluationDesc'),
      color: 'from-yellow-500 to-amber-600',
      features: [
        t('club.featureScore1to10'),
        t('club.featureDetailedComments'),
        t('club.featurePersonalRecs'),
        t('club.featurePerformanceDiscount'),
      ],
    },
    {
      id: 'gamification',
      icon: Gamepad2,
      title: t('club.gamification'),
      description: t('club.gamificationDesc'),
      color: 'from-purple-500 to-pink-600',
      features: [
        t('club.featureFifaCard'),
        t('club.featureOverallRating'),
        t('club.featureHexRadar'),
        t('club.featureLevelXP'),
      ],
    },
  ];

  const SUBSCRIPTION_PLANS = [
    {
      id: 'basic',
      name: t('club.planBasic'),
      duration: t('club.planBasicDuration'),
      clubs: 1,
      price: 19000,
      features: [
        t('club.planBasicF1'),
        t('club.planBasicF2'),
        t('club.planBasicF3'),
        t('club.planBasicF4'),
      ],
      popular: false,
      color: 'border-dark-600',
    },
    {
      id: 'advanced',
      name: t('club.planAdvanced'),
      duration: t('club.planAdvancedDuration'),
      clubs: 3,
      price: 49000,
      features: [
        t('club.planAdvancedF1'),
        t('club.planAdvancedF2'),
        t('club.planAdvancedF3'),
        t('club.planAdvancedF4'),
        t('club.planAdvancedF5'),
      ],
      popular: true,
      color: 'border-brand-yellow',
    },
    {
      id: 'elite',
      name: t('club.planElite'),
      duration: t('club.planEliteDuration'),
      clubs: 6,
      price: 69000,
      features: [
        t('club.planEliteF1'),
        t('club.planEliteF2'),
        t('club.planEliteF3'),
        t('club.planEliteF4'),
        t('club.planEliteF5'),
      ],
      popular: false,
      color: 'border-fifa-teal',
    },
    {
      id: 'pro',
      name: t('club.planPro'),
      duration: t('club.planProDuration'),
      clubs: 10,
      price: 99000,
      features: [
        t('club.planProF1'),
        t('club.planProF2'),
        t('club.planProF3'),
        t('club.planProF4'),
        t('club.planProF5'),
      ],
      popular: false,
      color: 'border-purple-500',
    },
  ];

  const clubPlansToRender = SUBSCRIPTION_PLANS.map((plan) => ({
    ...plan,
    clubs: plan.clubs as number | undefined,
    priceText: formatMoney(
      plan.price,
      catalog?.country.currency ?? 'CLP',
      catalog?.country.locale,
    ),
  }));

  const TRAINING_MODULES = [
    { id: 'fundamentals', name: t('club.fundamentals'), lessons: 8, icon: Target, description: t('club.fundamentalsDesc') },
    { id: 'technique', name: t('club.technique'), lessons: 6, icon: Zap, description: t('club.techniqueDesc') },
    { id: 'physical', name: t('club.physical'), lessons: 10, icon: TrendingUp, description: t('club.physicalDesc') },
    { id: 'tactical', name: t('club.tactical'), lessons: 5, icon: Brain, description: t('club.tacticalDesc') },
    { id: 'preseason', name: t('club.preseason'), lessons: 15, icon: Medal, description: t('club.preseasonDesc') },
  ];

  const PLAYER_LEVELS = [
    { name: t('club.beginner'), xp: '0-500', color: 'text-gray-400' },
    { name: t('club.amateur'), xp: '500-1500', color: 'text-green-400' },
    { name: t('club.semiPro'), xp: '1500-3000', color: 'text-cyan-400' },
    { name: t('club.professional'), xp: '3000-5000', color: 'text-blue-400' },
    { name: t('club.elite'), xp: '5000-8000', color: 'text-purple-400' },
    { name: t('club.legend'), xp: '8000+', color: 'text-yellow-400' },
  ];

  return (
    <section id="club" className="relative py-24 md:py-32 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-brand-navy via-brand-darkNavy to-brand-navy" />
      
      {/* Animated background pattern */}
      <div className="absolute inset-0">
        <div 
          className="absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,215,0,0.5) 1px, transparent 0)`,
            backgroundSize: '50px 50px',
          }}
        />
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-brand-yellow/5 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-fifa-teal/5 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div 
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-brand-yellow/20 to-amber-500/10 border border-brand-yellow/30 mb-6"
            initial={{ scale: 0.9, opacity: 0 }}
            whileInView={{ scale: 1, opacity: 1 }}
            viewport={{ once: true }}
          >
            <Sparkles className="w-4 h-4 text-brand-yellow" />
            <span className="text-sm font-bold text-brand-yellow tracking-wider">{t('club.badge')}</span>
          </motion.div>
          
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-white mb-6">
            CLUB
            <span className="block text-transparent bg-clip-text bg-gradient-to-r from-brand-yellow via-amber-400 to-brand-yellow">
              BTALENTSTAR
            </span>
          </h2>
          
          <p className="text-lg md:text-xl text-dark-300 max-w-3xl mx-auto">
            {t('club.subtitle')}
          </p>

          {/* App Store Badges Placeholder */}
          <div className="flex flex-wrap justify-center gap-4 mt-8">
            <motion.a
              href={STORE_LINKS.appStore}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center gap-3 px-6 py-3 rounded-xl bg-black border border-dark-700 hover:border-dark-500 transition-colors"
            >
              <svg className="w-8 h-8" viewBox="0 0 24 24" fill="white">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              <div className="text-left">
                <p className="text-xs text-dark-400">{t('club.downloadOn')}</p>
                <p className="text-white font-semibold">App Store</p>
              </div>
            </motion.a>
            
            <motion.a
              href={STORE_LINKS.googlePlay}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center gap-3 px-6 py-3 rounded-xl bg-black border border-dark-700 hover:border-dark-500 transition-colors"
            >
              <svg className="w-8 h-8" viewBox="0 0 24 24" fill="white">
                <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 010 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.802 8.99l-2.303 2.303-8.635-8.635z"/>
              </svg>
              <div className="text-left">
                <p className="text-xs text-dark-400">{t('club.availableOn')}</p>
                <p className="text-white font-semibold">Google Play</p>
              </div>
            </motion.a>
            <motion.a
              href={STORE_LINKS.appGallery}
              target="_blank"
              rel="noopener noreferrer"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center gap-3 px-6 py-3 rounded-xl bg-black border border-dark-700 hover:border-dark-500 transition-colors"
            >
              <Smartphone className="w-8 h-8 text-white" />
              <div className="text-left">
                <p className="text-xs text-dark-400">{t('club.availableOn')}</p>
                <p className="text-white font-semibold">AppGallery</p>
              </div>
            </motion.a>
          </div>
        </motion.div>

        {/* Features Grid */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="mb-20"
        >
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {APP_FEATURES.map((feature, i) => {
              const Icon = feature.icon;
              const isSelected = selectedFeature === i;
              
              return (
                <motion.div
                  key={feature.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  whileHover={{ y: -5, scale: 1.02 }}
                  onClick={() => setSelectedFeature(i)}
                  className={`
                    relative cursor-pointer p-6 rounded-2xl border backdrop-blur-sm overflow-hidden
                    transition-all duration-300
                    ${isSelected 
                      ? 'bg-gradient-to-br from-brand-navy/90 to-brand-darkNavy/90 border-brand-yellow/50 shadow-lg shadow-brand-yellow/10' 
                      : 'bg-brand-navy/60 border-brand-lightBlue/20 hover:border-brand-lightBlue/40'
                    }
                  `}
                >
                  <CardCorner position="top-left" />
                  <CardCorner position="bottom-right" />
                  
                  {/* Gradient bg */}
                  <div className={`absolute inset-0 bg-gradient-to-br ${feature.color} opacity-5`} />
                  
                  <div className="relative">
                    <div className={`
                      w-14 h-14 rounded-xl flex items-center justify-center mb-4
                      ${isSelected ? 'bg-brand-yellow/20' : 'bg-brand-lightBlue/10'}
                    `}>
                      <Icon className={`w-7 h-7 ${isSelected ? 'text-brand-yellow' : 'text-dark-300'}`} />
                    </div>
                    
                    <h3 className="text-lg font-bold text-white mb-2">{feature.title}</h3>
                    <p className="text-dark-400 text-sm">{feature.description}</p>
                    
                    {isSelected && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="mt-4 pt-4 border-t border-brand-lightBlue/20"
                      >
                        {feature.skills && (
                          <div className="flex flex-wrap gap-1">
                            {feature.skills.map((skill) => (
                              <span key={skill} className="px-2 py-1 text-xs rounded bg-brand-yellow/10 text-brand-yellow">
                                {skill}
                              </span>
                            ))}
                          </div>
                        )}
                        {feature.features && (
                          <div className="space-y-2">
                            {feature.features.slice(0, 3).map((f, j) => (
                              <div key={j} className="flex items-center gap-2">
                                <CheckCircle className="w-3 h-3 text-fifa-teal flex-shrink-0" />
                                <span className="text-xs text-dark-300">{f}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* AI & Gamification Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mb-20"
        >
          <div className="grid lg:grid-cols-2 gap-8">
            {/* AI Pose Detection */}
            <div className="relative bg-gradient-to-br from-brand-navy/80 to-brand-darkNavy/80 backdrop-blur-xl rounded-2xl border border-fifa-teal/20 p-8 overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-fifa-teal to-transparent" />
              <div className="absolute -top-20 -right-20 w-40 h-40 bg-fifa-teal/10 rounded-full blur-3xl" />
              
              <div className="relative">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-fifa-teal/20 flex items-center justify-center">
                    <Brain className="w-6 h-6 text-fifa-teal" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{t('club.aiTitle')}</h3>
                    <p className="text-dark-400 text-sm">{t('club.aiSubtitle')}</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-brand-darkNavy/50 border border-brand-lightBlue/10">
                    <div className="w-10 h-10 rounded-lg bg-fifa-teal/20 flex items-center justify-center">
                      <Camera className="w-5 h-5 text-fifa-teal" />
                    </div>
                    <div>
                      <p className="text-white font-medium">MediaPipe ML Kit</p>
                      <p className="text-dark-400 text-sm">{t('club.posePoints')}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-brand-darkNavy/50 border border-brand-lightBlue/10">
                    <div className="w-10 h-10 rounded-lg bg-fifa-teal/20 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-fifa-teal" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{t('club.jumpDetection')}</p>
                      <p className="text-dark-400 text-sm">{t('club.jumpHeight')}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4 p-4 rounded-xl bg-brand-darkNavy/50 border border-brand-lightBlue/10">
                    <div className="w-10 h-10 rounded-lg bg-fifa-teal/20 flex items-center justify-center">
                      <Video className="w-5 h-5 text-fifa-teal" />
                    </div>
                    <div>
                      <p className="text-white font-medium">Skeleton Overlay</p>
                      <p className="text-dark-400 text-sm">{t('club.realTimeViz')}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Gamification - FIFA Card Style */}
            <div className="relative bg-gradient-to-br from-brand-navy/80 to-brand-darkNavy/80 backdrop-blur-xl rounded-2xl border border-brand-yellow/20 p-8 overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-brand-yellow to-transparent" />
              <div className="absolute -top-20 -left-20 w-40 h-40 bg-brand-yellow/10 rounded-full blur-3xl" />
              
              <div className="relative">
                <div className="flex items-center gap-3 mb-6">
                  <div className="w-12 h-12 rounded-xl bg-brand-yellow/20 flex items-center justify-center">
                    <Gamepad2 className="w-6 h-6 text-brand-yellow" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white">{t('club.fifaProfile')}</h3>
                    <p className="text-dark-400 text-sm">{t('club.fifaCard')}</p>
                  </div>
                </div>

                {/* Mini FIFA Card Preview */}
                <div className="relative bg-gradient-to-br from-brand-yellow/20 to-amber-600/10 rounded-xl p-6 border border-brand-yellow/30 mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-lg bg-brand-darkNavy flex items-center justify-center border border-brand-yellow/30">
                      <span className="text-3xl font-black text-brand-yellow">87</span>
                    </div>
                    <div>
                      <p className="text-white font-bold text-lg">{t('club.yourName')}</p>
                      <p className="text-brand-yellow text-sm font-medium">{t('club.forward')}</p>
                      <div className="flex gap-2 mt-1">
                        <span className="text-xs text-dark-400">PAC 85</span>
                        <span className="text-xs text-dark-400">SHO 88</span>
                        <span className="text-xs text-dark-400">DRI 90</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Levels */}
                <div className="space-y-2">
                  <p className="text-sm font-bold text-white mb-3">{t('club.levelSystem')}</p>
                  <div className="flex flex-wrap gap-2">
                    {PLAYER_LEVELS.map((level) => (
                      <span 
                        key={level.xp}
                        className={`px-3 py-1 rounded-lg bg-brand-darkNavy/50 border border-brand-lightBlue/10 text-xs font-medium ${level.color}`}
                      >
                        {level.name}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Training Modules */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mb-20"
        >
          <h3 className="text-2xl md:text-3xl font-black text-white mb-8 text-center">
            📚 {t('club.trainingModules')}
          </h3>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {TRAINING_MODULES.map((module, i) => {
              const Icon = module.icon;
              const isPremium = module.id === 'preseason';
              
              return (
                <motion.div
                  key={module.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  whileHover={{ y: -3 }}
                  className={`
                    relative p-5 rounded-xl border backdrop-blur-sm
                    ${isPremium 
                      ? 'bg-gradient-to-br from-brand-yellow/10 to-amber-500/5 border-brand-yellow/30' 
                      : 'bg-brand-navy/60 border-brand-lightBlue/20'
                    }
                  `}
                >
                  {isPremium && (
                    <div className="absolute -top-2 -right-2 px-2 py-0.5 rounded-full bg-brand-yellow text-brand-darkNavy text-xs font-bold">
                      {t('club.premium')}
                    </div>
                  )}
                  
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
                    isPremium ? 'bg-brand-yellow/20' : 'bg-brand-lightBlue/10'
                  }`}>
                    <Icon className={`w-5 h-5 ${isPremium ? 'text-brand-yellow' : 'text-dark-300'}`} />
                  </div>
                  
                  <h4 className="text-white font-bold mb-1">{module.name}</h4>
                  <p className="text-dark-400 text-xs mb-2">{module.description}</p>
                  <p className="text-fifa-teal text-sm font-medium">{module.lessons} {t('club.lessons')}</p>
                </motion.div>
              );
            })}
          </div>
        </motion.div>

        {/* Subscription Plans Toggle */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          className="text-center mb-8"
        >
          <button
            onClick={() => setShowPlans(!showPlans)}
            className="inline-flex items-center gap-2 px-6 py-3 rounded-xl bg-brand-navy/60 border border-brand-lightBlue/30 text-white font-bold hover:border-brand-yellow/50 transition-colors"
          >
            <Gift className="w-5 h-5 text-brand-yellow" />
            {showPlans ? t('club.hidePlans') : t('club.viewPlans')}
            <ChevronRight className={`w-5 h-5 transition-transform ${showPlans ? 'rotate-90' : ''}`} />
          </button>
        </motion.div>

        {/* Subscription Plans */}
        <AnimatePresence>
          {showPlans && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
              className="mb-20 overflow-hidden"
            >
              {catalog?.providers && catalog.providers.length > 0 && (
                <p className="text-center text-sm text-dark-400 mb-6">
                  {catalog.country.name}: {catalog.providers.map((p) => p.display_name).join(' · ')}
                </p>
              )}
              <div className={`grid sm:grid-cols-2 ${clubPlansToRender.length >= 5 ? 'lg:grid-cols-5' : 'lg:grid-cols-4'} gap-6`}>
                {clubPlansToRender.map((plan, i) => (
                  <motion.div
                    key={plan.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.1 }}
                    className={`
                      relative p-6 rounded-2xl border-2 backdrop-blur-sm
                      ${plan.popular 
                        ? 'bg-gradient-to-br from-brand-yellow/10 to-amber-500/5 ' + plan.color
                        : 'bg-brand-navy/60 ' + plan.color
                      }
                    `}
                  >
                    {plan.popular && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-brand-yellow text-brand-darkNavy text-xs font-bold">
                        {t('club.mostPopular')}
                      </div>
                    )}
                    
                    <CardCorner position="top-left" />
                    <CardCorner position="bottom-right" />
                    
                    <div className="text-center mb-6">
                      <h4 className="text-xl font-bold text-white mb-1">{plan.name}</h4>
                      <p className="text-dark-400 text-sm">{plan.duration}</p>
                      <div className="mt-4">
                        <span className="text-3xl font-black text-brand-yellow">
                          {plan.priceText}
                        </span>
                      </div>
                      {plan.clubs != null && plan.clubs > 0 && (
                        <p className="text-fifa-teal text-sm mt-2">
                          <Trophy className="w-4 h-4 inline mr-1" />
                          {plan.clubs} {plan.clubs === 1 ? 'club' : t('club.clubs')}
                        </p>
                      )}
                    </div>
                    
                    <div className="space-y-3 mb-6">
                      {plan.features.map((feature, j) => (
                        <div key={j} className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-fifa-teal flex-shrink-0 mt-0.5" />
                          <span className="text-dark-300 text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                    
                    <Button 
                      variant={plan.popular ? 'primary' : 'outline'} 
                      fullWidth
                      size="sm"
                      onClick={() => setIsWizardOpen(true)}
                    >
                      {t('club.select')}
                    </Button>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          className="text-center"
        >
          <AnimatedBorder className="inline-block">
            <div className="px-8 py-6 bg-brand-navy/80">
              <div className="flex items-center justify-center gap-2 mb-2">
                <Smartphone className="w-5 h-5 text-brand-yellow" />
                <p className="text-white font-bold text-lg">
                  {t('club.downloadApp')}
                </p>
              </div>
              <p className="text-dark-400 mb-4">
                {t('club.demoIncluded')}
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                <Button variant="primary" icon={<ArrowRight className="w-4 h-4" />} iconPosition="right">
                  {t('club.downloadNow')}
                </Button>
                <Button variant="outline" icon={<Play className="w-4 h-4" />} iconPosition="left">
                  {t('club.watchDemo')}
                </Button>
              </div>
            </div>
          </AnimatedBorder>
        </motion.div>
      </div>

      <RegistrationWizard isOpen={isWizardOpen} onClose={() => setIsWizardOpen(false)} />
    </section>
  );
}
